import { Observable } from 'rxjs';
import { ReadOptions } from '@app/domain/models/read-options.model';
import { TpvCompany } from '@app/domain/models/tpv/company-directory.model';
import { TpvCompanyDetail } from '@app/domain/models/tpv/entity-detail.model';
import { TpvListQuery, TpvPage } from '@app/domain/models/tpv/tpv-page.model';

export interface CompanyListQuery extends TpvListQuery {
  socialName?: string;
  idCompany?: string;
  entity?: string;
  /** Estado(s) a incluir: `ACTIVO` / `INACTIVO` / `ELIMINADO`. Se envía como CSV. */
  status?: string[];
  /** `true` ⇒ pide SOLO eliminadas (`?deleted=true`); el backend ignora `status`. */
  deleted?: boolean;
  /**
   * Búsqueda server-side (contrato 2026-09-29): el micro TPV hace OR entre las
   * columnas visibles del directorio de empresas — nombre, razón social y RFC.
   * A diferencia de `merchantName` (que se mapea a `?name=`, aplicado ANTES de
   * llegar la fila), `?search=` no descarta una empresa cuyo RFC coincide pero
   * cuyo nombre no.
   */
  search?: string;
}

/**
 * Port de lectura del directorio de empresas.
 * Sin `id_merchant`; los filtros son por nombre, razón social, id, padre y entidad.
 *
 * `options` (`ReadOptions`, dato PURO de dominio) va como SEGUNDO parámetro y
 * nunca dentro del query: el query se serializa a parámetros de URL en
 * `toHttpParams`, y una opción de transporte metida ahí acabaría viajando como
 * `?silent=true` al backend. Son dos cosas distintas y se mantienen separadas.
 */
export abstract class CompanyDirectoryRepository {
  abstract getCompanies(
    query?: CompanyListQuery,
    options?: ReadOptions,
  ): Observable<TpvPage<TpvCompany>>;

  /**
   * Detalle (passthrough live) de una empresa: `GET /companies/<org_id_company>/`.
   * Trae identidad + dirección + contacto + banco completos. ⚠️ El path usa el
   * **PK ENTERO de la org** (`org_id_company`), NO el UUID (el UUID da 404).
   */
  abstract getById(orgIdCompany: number | string): Observable<TpvCompanyDetail>;
}
